import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { uniq } from 'lodash';
import { useClientUser } from '@/hooks';
import { CATALOG_SERVICE_RATES } from '@/constants/featureFlags';
import {
    QUERY_SERVICE_RATE_TARIFFS,
    QUERY_SERVICE_RATES,
    UPSERT_SERVICE_CATALOG,
    UPSERT_SERVICE_CATALOG_WITH_RATES,
} from './graphql';
import {
    applyScopedNonBillable,
    buildServiceCatalogPayload,
    buildServiceRateChange,
    scopedNonBillableApplies,
    serviceCatalogFormValues,
} from './utils';

// The catalog-service-rates flag, read off the signed-in account: on the Account page and in the
// Edit Order quick add alike, that's the shipper whose catalog is being edited.
export const useCatalogServiceRatesEnabled = () => {
    const { circles } = useClientUser();
    return !!circles?.[CATALOG_SERVICE_RATES];
};

// What a catalog service's rate needs: the GE tariffs it goes on and the SERVICE_RATE rows already on
// them. Nothing loads with the flag off or no shipper. `rows` is only an answer once `ready`: while
// either lookup is `loading`, or after one fails (`error`), it's empty for want of one, and treating
// that as "no rate" would let a save clear the service's rows.
export const useCatalogServiceRates = (shipperId) => {
    const enabled = useCatalogServiceRatesEnabled();
    const skip = !enabled || !shipperId;

    const {
        data: tariffData,
        loading: tariffsLoading,
        error: tariffsError,
    } = useQuery(QUERY_SERVICE_RATE_TARIFFS, {
        variables: { client_id: shipperId },
        skip,
    });
    const tariffIds = useMemo(
        () => uniq((tariffData?.pricing_overrides || []).map((override) => override?.tariff_id).filter(Boolean)),
        [tariffData]
    );

    const {
        data: ratesData,
        loading: ratesLoading,
        error: ratesError,
    } = useQuery(QUERY_SERVICE_RATES, {
        variables: { tariff_ids: tariffIds },
        skip: skip || tariffIds.length === 0,
    });
    const rows = useMemo(() => ratesData?.rates || [], [ratesData]);

    // A failed refetch keeps the old rows but sets the error, so an error always counts, rows or not.
    const error = skip ? null : tariffsError || ratesError || null;
    // Both lookups have answered: the tariffs, then the rates on them (nothing to ask without a tariff).
    const answered = !!tariffData && (tariffIds.length === 0 || !!ratesData);
    const ready = !skip && !error && answered && !tariffsLoading && !ratesLoading;

    return { enabled, tariffIds, rows, ready, loading: !skip && !error && !ready, error };
};

// Saves a catalog entry, given useCatalogServiceRates' result. With the flag on, a tariff to write to
// and the rates `ready`, its rate goes on the tariffs in the same transaction; otherwise only the
// catalog is written. The form's Rate seeds from those rows and a rate save clears them first, so one
// made before they load, or after the lookup fails, would wipe the service's rate: it leaves the rows
// as they are instead. Rejects on failure.
export const useSaveServiceCatalog = ({ enabled, tariffIds, ready } = {}) => {
    const [upsertService, { loading: catalogSaving }] = useMutation(UPSERT_SERVICE_CATALOG);
    const [upsertServiceWithRates, { loading: ratesSaving }] = useMutation(UPSERT_SERVICE_CATALOG_WITH_RATES, {
        refetchQueries: ['ServiceRates'],
        awaitRefetchQueries: true,
    });

    const save = useCallback(
        (object, serviceRate) => {
            const change = enabled && ready ? buildServiceRateChange(object, serviceRate, tariffIds) : null;
            return change
                ? upsertServiceWithRates({ variables: { object, ...change } })
                : upsertService({ variables: { object } });
        },
        [enabled, ready, tariffIds, upsertService, upsertServiceWithRates]
    );

    return { save, saving: catalogSaving || ratesSaving };
};

// Field state for ServiceCatalogModal. Reseeds every time the modal opens (add => blank, or
// `defaults` when given; edit => the row) so a cancelled edit never leaks into the next open.
// `defaults` is catalog-row-shaped like `service`, and `serviceRate` is the entry's SERVICE_RATE row
// (findServiceRate). Keep both identities stable while the modal is open: a new object reseeds the
// form and drops what the operator typed. The one expected change is `serviceRate` landing after the
// modal opened; `ratesLoading` (useCatalogServiceRates' `loading`) locks the form until then, so that
// reseed has nothing to drop.
export const useServiceCatalogForm = (open, service, defaults, serviceRate, ratesLoading = false) => {
    const isEdit = !!service;
    const serviceRatesEnabled = useCatalogServiceRatesEnabled();
    const [values, setValues] = useState(() => serviceCatalogFormValues(service || defaults, serviceRate));

    useEffect(() => {
        if (!open) return;
        setValues(serviceCatalogFormValues(service || defaults, serviceRate));
    }, [open, service, defaults, serviceRate]);

    const setField = (field) => (value) => setValues((prev) => ({ ...prev, [field]: value }));

    // Dimensions is a map, so a blank selection removes the key rather than setting ''.
    const setDimension = (key) => (value) =>
        setValues((prev) => {
            const dimensions = { ...prev.dimensions };
            if (`${value ?? ''}`.trim()) dimensions[key] = `${value}`.trim();
            else delete dimensions[key];
            return { ...prev, dimensions };
        });

    // Scoping a new service forces it non-billable, so the fields render and save forced —
    // `values` stays the operator's raw input underneath.
    const lockOptions = { isEdit, serviceRatesEnabled };
    const effective = applyScopedNonBillable(values, lockOptions);

    return {
        values: effective,
        setField,
        setDimension,
        nonBillableLocked: scopedNonBillableApplies(values, lockOptions),
        serviceRatesEnabled,
        locked: !!ratesLoading,
        // Code is the only required field; it's half of the row's uniqueness constraint.
        canSave: !ratesLoading && !!`${values.code ?? ''}`.trim(),
        payload: () => buildServiceCatalogPayload(effective),
        // The rate, plus the row's own qbo_tag carried through, for buildServiceRateChange.
        ratePayload: () => ({ rate: effective.rate, qboTag: effective.qboTag }),
    };
};
